/**
 * The referee's page — /reference/:token
 *
 * A pastor, tutor or employer answers privately. They see the applicant's name
 * and programme, nothing more, and the applicant never sees what they wrote.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RECOMMENDATIONS = ['strongly recommend', 'recommend',
  'recommend with reservations', 'do not recommend'];

export default function ReferencePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    relationship: '', referee_role: '', known_for: '',
    recommendation: RECOMMENDATIONS[0], comments: '',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    fetch(`${API}/api/public/admissions/references/${token}`)
      .then((r) => r.json())
      .then((d) => { setInfo(d); setDone(!!d.already_submitted); })
      .catch(() => setInfo({ error: true }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/public/admissions/references/${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail
        : 'Could not send that — please try again.');
      setDone(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!info) {
    return <div className="min-h-screen grid place-items-center">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>;
  }

  if (info.error || info.detail) {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="reference-invalid">
        <Card className="max-w-md"><CardContent className="py-10 text-center space-y-2">
          <p className="font-semibold text-slate-900">That link is no longer valid</p>
          <p className="text-sm text-slate-600">
            Please ask the applicant to send you a fresh reference request.
          </p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-10" data-testid="reference-page">
      <Card className="max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-600" />
            {info.reference_type ? `${info.reference_type} reference` : 'Reference'}
          </CardTitle>
          <p className="text-sm text-slate-600">
            {info.applicant_name} has applied to study {info.programme_name || 'with us'} at{' '}
            {info.school_name || 'our college'} and named you as a referee. Your answers are
            seen by the admissions team only.
          </p>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="py-8 text-center space-y-3" data-testid="reference-done">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
              <p className="font-semibold text-slate-900">Thank you</p>
              <p className="text-sm text-slate-600">
                Your reference has been received. There's nothing else to do.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="rel" className="text-sm">How do you know the applicant?</Label>
                <Input id="rel" required value={form.relationship} onChange={set('relationship')}
                       placeholder="Their pastor, tutor, employer…"
                       data-testid="reference-relationship" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="kn" className="text-sm">For how long?</Label>
                <Input id="kn" value={form.known_for} onChange={set('known_for')}
                       placeholder="Six years" data-testid="reference-known-for" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rc" className="text-sm">Your recommendation</Label>
                <select id="rc" value={form.recommendation} onChange={set('recommendation')}
                        data-testid="reference-recommendation"
                        className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm capitalize">
                  {RECOMMENDATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cm" className="text-sm">Your comments</Label>
                <Textarea id="cm" rows={5} required minLength={5} value={form.comments}
                          onChange={set('comments')} data-testid="reference-comments"
                          placeholder="Their character, their calling, and how they'd cope with study." />
              </div>
              <Button type="submit" className="w-full" disabled={busy}
                      data-testid="reference-submit">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Send my reference
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
